import type { ReactNode } from 'react';

export interface CmsSectionStateProps {
  readonly loading: boolean;
  readonly error: boolean;
  /** True when data loaded successfully but there is nothing to render. */
  readonly empty?: boolean;
  /** sr-only label announced while the spinner is shown. */
  readonly loadingLabel: string;
  /** Body text-color utility (t.body) for the error / empty message. */
  readonly bodyClass: string;
  readonly errorMessage: string;
  readonly emptyMessage?: string;
  /** Rendered only in the success case (not loading, not error, not empty). */
  readonly children: ReactNode;
}

/**
 * The shared loading / error / empty triad for data-backed CMS sections.
 * Renders exactly one of: a spinner, an error message, an empty message, or the
 * children (the loaded content). The spinner is theme-neutral (always the green
 * mark) to match the existing sections.
 */
export function CmsSectionState({
  loading,
  error,
  empty = false,
  loadingLabel,
  bodyClass,
  errorMessage,
  emptyMessage,
  children,
}: CmsSectionStateProps) {
  if (loading) {
    return (
      <div className="flex justify-center py-10" role="status" aria-live="polite">
        <span className="inline-block w-8 h-8 rounded-full border-2 border-mf-green/30 border-t-mf-green animate-spin" />
        <span className="sr-only">{loadingLabel}</span>
      </div>
    );
  }
  if (error) return <p className={bodyClass}>{errorMessage}</p>;
  if (empty) return <p className={bodyClass}>{emptyMessage}</p>;
  return <>{children}</>;
}
